/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import ChevronForwardIcon from '../../../icons/current/ChevronForwardIcon';
import {BreadcrumbItem} from './BreadcrumbItem';

export interface BreadcrumbItemData {
  label: string;
  isActive?: boolean;
  onClick?: () => void;
}

export interface BreadcrumbProps {
  items: BreadcrumbItemData[];
}

const useStyles = makeStyles(() => ({
  // a trail too long for one line wraps onto the next, rather than shrinking
  // the current item to a letter or two - e.g. "Asset > LMU3640_QA_8930 >
  // Tool 72308628872115583". An item longer than a whole line still ends in
  // an ellipsis (BreadcrumbItem), so the header's actions keep their place ...
  root : {
    minWidth   : 0,
    boxSizing  : 'border-box',
    display    : 'flex',
    flexWrap   : 'wrap',
    alignItems : 'center',
    gap        : '4px',
    padding    : '8px'
  },
  separator : {
    flexShrink : 0,
    display    : 'flex',
    alignItems : 'center'
  }
}));

const getItemKey = (item: BreadcrumbItemData, index: number): string => `${item.label}-${index}`;

/**
 * Breadcrumb renders the Drawer Header's crumb trail (e.g. "Assets ›
 * Vehicle 13 › Vision") as a sequence of `BreadcrumbItem`s separated by the
 * shared `ChevronForwardIcon` (`@ion/dls/src/icons/current/ChevronForwardIcon`),
 * per the Figma "Drawer Header" breadcrumb separator export - distinct from
 * `components/breadcrumbs/Breadcrumbs.tsx`'s own text `separator: '>'`,
 * which this ticket's Figma spec doesn't reuse.
 *
 * ```typescript
 *
 *   import {Breadcrumb} from
 *     '@ion/dls/src/components/drawer/current/Breadcrumb';
 *   ...
 *
 *   <Breadcrumb items={[
 *     {label: 'Assets', onClick: onAssetsClick},
 *     {label: 'Vehicle 13', onClick: onAssetClick},
 *     {label: 'Vision', isActive: true}
 *   ]} />
 *
 * ```
 *
 */
export const Breadcrumb = memo(({items}: BreadcrumbProps) => {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      {items.map(function(item, index) {
        return (
          <React.Fragment key={getItemKey(item, index)}>
            {index > 0 && (
              <span className={classes.separator}>
                <ChevronForwardIcon />
              </span>
            )}
            <BreadcrumbItem label={item.label}
              isActive={item.isActive}
              onClick={item.onClick} />
          </React.Fragment>
        );
      })}
    </div>
  );
});

export default Breadcrumb;
